import { Box, Tooltip } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { useMemo } from "react";
import { useRoleCellFittingItems } from "~/components/Members/hooks/useRoleCellFittingItems";
import { BaseScopeNode } from "./columns";

type Contact = NonNullable<BaseScopeNode["contacts"]>[number];

function getDisplayName(contact: Contact): string {
  return contact.name || contact.email || contact.identity;
}

function formatTooltipLine(contact: Contact): string {
  const name = getDisplayName(contact);
  if (contact.email && contact.email !== name) {
    return `${name} (${contact.email})`;
  }
  return name;
}

export function ScopeTableContactsCell(
  params: GridRenderCellParams<BaseScopeNode>,
) {
  const contacts = params.row.contacts ?? [];

  const contactNames = useMemo(() => contacts.map(getDisplayName), [contacts]);

  const { containerRef, measureRef, hasHidden, displayText } =
    useRoleCellFittingItems({ items: contactNames });

  if (contacts.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "100%",
        }}
      >
        —
      </Box>
    );
  }

  const tooltipContent = contacts.map(formatTooltipLine).join("\n");

  const content =
    hasHidden || contacts.some((c) => c.email) ? (
      <Tooltip
        title={<span style={{ whiteSpace: "pre-line" }}>{tooltipContent}</span>}
        placement="top"
        arrow
      >
        <span style={{ cursor: "help" }}>{displayText}</span>
      </Tooltip>
    ) : (
      displayText
    );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: "100%",
        width: "100%",
      }}
    >
      <Box ref={containerRef} sx={{ width: 1, whiteSpace: "nowrap" }}>
        <span
          ref={measureRef}
          aria-hidden="true"
          style={{
            position: "absolute",
            visibility: "hidden",
            whiteSpace: "nowrap",
          }}
        />
        {content}
      </Box>
    </Box>
  );
}
